import axios, { AxiosError, type AxiosRequestConfig } from "axios";

/**
 * The single axios instance every page and query goes through.
 *
 * Two credentials are in play and they are deliberately different:
 *  - the ACCESS token is short-lived (15 min) and travels in the Authorization header;
 *  - the REFRESH token lives only in an httpOnly cookie the browser sends automatically
 *    (hence `withCredentials`) and is never readable from JS. That is why refreshing works
 *    without us storing the refresh token anywhere.
 */
export const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3000/api/v1";

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// --- token plumbing -------------------------------------------------------
// Kept module-local rather than imported from the store to avoid a cycle: the store imports
// this file. `setAccessToken` is the one seam the store uses to push changes in.

const ACCESS_TOKEN_KEY = "smsbridge.accessToken";

let accessToken: string | null = localStorage.getItem(ACCESS_TOKEN_KEY);

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

/** Called when refreshing fails — lets the auth store tear down without importing it here. */
let onAuthExpired: (() => void) | null = null;
export function setOnAuthExpired(handler: () => void): void {
  onAuthExpired = handler;
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// --- refresh-on-401 -------------------------------------------------------

/**
 * Single-flight: if five queries 401 at once we must not fire five refreshes — the backend
 * rotates refresh tokens and treats a replay as theft, revoking the whole family. So the first
 * 401 owns the refresh and everyone else awaits the same promise.
 */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  refreshPromise ??= axios
    .post<{ accessToken: string }>(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    )
    .then((res) => {
      setAccessToken(res.data.accessToken);
      return res.data.accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    const isAuthEndpoint =
      typeof original?.url === "string" &&
      (original.url.includes("/auth/login") ||
        original.url.includes("/auth/register") ||
        original.url.includes("/auth/refresh"));

    // Only a 401 on a normal request is refreshable. A 401 from login itself means "wrong
    // password" and must surface to the form, not trigger a refresh loop.
    if (status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        const token = await refreshAccessToken();
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
        return api.request(original);
      } catch {
        setAccessToken(null);
        onAuthExpired?.();
      }
    }

    return Promise.reject(error);
  },
);

// --- error shaping --------------------------------------------------------

interface ApiErrorBody {
  error?: { code?: string; message?: string; requestId?: string };
}

/**
 * The API's GlobalExceptionFilter always answers with `{ error: { code, message } }`.
 * Pull the human-readable half out of that so components never render "[object Object]".
 */
export function apiErrorMessage(err: unknown, fallback = "Something went wrong."): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error?.message) return body.error.message;
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the server. Is the API running on port 3000?";
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** The machine-readable half, for branching on specific failures. */
export function apiErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    return body?.error?.code ?? null;
  }
  return null;
}
